import { Badge, Button, Card, Input, Label, Spinner } from '@fc/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Eye, Globe, Save } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { PageHeader } from '../../components/page-header'
import {
  createSermon,
  getSermon,
  publishSermon,
  unpublishSermon,
  updateSermon,
} from '../../lib/content-api'
import type { BodyFormat, ContentStatus, SermonInput } from '../../lib/content-api'
import { ContentBody } from './markdown'
import { Field, Select, Textarea, slugify, statusBadgeVariant } from './fields'

interface FormState {
  title: string
  slug: string
  series: string
  speaker: string
  scripture: string
  summary: string
  bodyFormat: BodyFormat
  body: string
  audioUrl: string
  videoUrl: string
  preachedAt: string
}

const EMPTY: FormState = {
  title: '',
  slug: '',
  series: '',
  speaker: '',
  scripture: '',
  summary: '',
  bodyFormat: 'markdown',
  body: '',
  audioUrl: '',
  videoUrl: '',
  preachedAt: '',
}

/** Convert an ISO timestamp to the value a <input type=date> expects. */
function toDateInput(iso: string | null): string {
  if (!iso) return ''
  return new Date(iso).toISOString().slice(0, 10)
}

export function SermonEditor() {
  const { id } = useParams<{ id: string }>()
  const isNew = !id || id === 'new'
  const navigate = useNavigate()
  const qc = useQueryClient()

  const [form, setForm] = useState<FormState>(EMPTY)
  const [slugTouched, setSlugTouched] = useState(false)
  const [status, setStatus] = useState<ContentStatus>('draft')
  const [showPreview, setShowPreview] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }))

  const detail = useQuery({
    queryKey: ['content', 'sermons', id],
    queryFn: () => getSermon(id!),
    enabled: !isNew,
  })

  useEffect(() => {
    if (detail.data) {
      const s = detail.data
      setForm({
        title: s.title,
        slug: s.slug,
        series: s.series ?? '',
        speaker: s.speaker ?? '',
        scripture: s.scripture ?? '',
        summary: s.summary ?? '',
        bodyFormat: s.bodyFormat,
        body: s.body,
        audioUrl: s.audioUrl ?? '',
        videoUrl: s.videoUrl ?? '',
        preachedAt: toDateInput(s.preachedAt),
      })
      setStatus(s.status)
      setSlugTouched(true)
    }
  }, [detail.data])

  const payload = (): SermonInput => ({
    title: form.title.trim(),
    slug: form.slug.trim(),
    series: form.series.trim() || null,
    speaker: form.speaker.trim() || null,
    scripture: form.scripture.trim() || null,
    summary: form.summary.trim() || null,
    bodyFormat: form.bodyFormat,
    body: form.body,
    audioUrl: form.audioUrl.trim() || null,
    videoUrl: form.videoUrl.trim() || null,
    preachedAt: form.preachedAt ? new Date(form.preachedAt).toISOString() : null,
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: ['content', 'sermons'] })

  const saveMut = useMutation({
    mutationFn: async () => (isNew ? createSermon(payload()) : updateSermon(id!, payload())),
    onSuccess: (saved) => {
      setFormError(null)
      invalidate()
      if (isNew) navigate(`/app/content/sermons/${saved.id}`, { replace: true })
    },
    onError: (e) => setFormError(e instanceof Error ? e.message : 'Could not save sermon.'),
  })

  const publishMut = useMutation({
    mutationFn: () => (status === 'published' ? unpublishSermon(id!) : publishSermon(id!)),
    onSuccess: (saved) => {
      setStatus(saved.status)
      invalidate()
    },
  })

  const canSave = form.title.trim().length > 0 && form.slug.trim().length > 0

  if (!isNew && detail.isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-20 text-muted">
        <Spinner /> Loading sermon…
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 text-sm text-muted">
        <Link to="/app/content/sermons" className="inline-flex items-center gap-1 hover:text-fg">
          <ArrowLeft className="size-4" /> Sermons
        </Link>
      </div>

      <PageHeader
        title={isNew ? 'New sermon' : form.title || 'Edit sermon'}
        description={isNew ? 'Add a sermon to the library.' : 'Edit sermon details.'}
        actions={
          <>
            {!isNew && (
              <Badge variant={statusBadgeVariant(status)} className="capitalize">
                {status}
              </Badge>
            )}
            <Button variant="secondary" onClick={() => setShowPreview((v) => !v)}>
              <Eye />
              {showPreview ? 'Hide preview' : 'Preview'}
            </Button>
            {!isNew && (
              <Button
                variant="outline"
                onClick={() => publishMut.mutate()}
                disabled={publishMut.isPending}
              >
                <Globe />
                {status === 'published' ? 'Unpublish' : 'Publish'}
              </Button>
            )}
            <Button onClick={() => saveMut.mutate()} disabled={!canSave || saveMut.isPending}>
              {saveMut.isPending ? <Spinner /> : <Save />}
              Save
            </Button>
          </>
        }
      />

      {formError && (
        <div className="rounded-lg border border-danger/40 bg-danger/10 px-4 py-3 text-sm text-danger">
          {formError}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[1fr,340px]">
        <Card className="space-y-5 p-5">
          <Field label="Title" htmlFor="sermon-title">
            <Input
              id="sermon-title"
              value={form.title}
              placeholder="The Good Shepherd"
              onChange={(e) => {
                set('title', e.target.value)
                if (isNew && !slugTouched) set('slug', slugify(e.target.value))
              }}
            />
          </Field>

          <Field label="Slug" htmlFor="sermon-slug" hint="Lowercase, hyphenated. Unique per site.">
            <Input
              id="sermon-slug"
              value={form.slug}
              placeholder="the-good-shepherd"
              onChange={(e) => {
                setSlugTouched(true)
                set('slug', slugify(e.target.value))
              }}
            />
          </Field>

          <Field label="Summary" htmlFor="sermon-summary" hint="One or two sentence teaser.">
            <Textarea
              id="sermon-summary"
              value={form.summary}
              onChange={(e) => set('summary', e.target.value)}
              className="min-h-20"
            />
          </Field>

          <Field
            label="Notes / transcript"
            htmlFor="sermon-body"
            hint={
              form.bodyFormat === 'markdown'
                ? 'Supports # headings, **bold**, *italic*, lists, links.'
                : undefined
            }
          >
            <Textarea
              id="sermon-body"
              value={form.body}
              onChange={(e) => set('body', e.target.value)}
              className="min-h-64 font-mono"
              placeholder="Sermon notes or transcript…"
            />
          </Field>
        </Card>

        <div className="space-y-6">
          <Card className="space-y-5 p-5">
            <Label>Details</Label>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Series" htmlFor="sermon-series">
                <Input
                  id="sermon-series"
                  value={form.series}
                  onChange={(e) => set('series', e.target.value)}
                  placeholder="Psalms"
                />
              </Field>
              <Field label="Speaker" htmlFor="sermon-speaker">
                <Input
                  id="sermon-speaker"
                  value={form.speaker}
                  onChange={(e) => set('speaker', e.target.value)}
                  placeholder="Pastor Jane"
                />
              </Field>
            </div>
            <Field label="Scripture" htmlFor="sermon-scripture">
              <Input
                id="sermon-scripture"
                value={form.scripture}
                onChange={(e) => set('scripture', e.target.value)}
                placeholder="John 10:1–18"
              />
            </Field>
            <Field label="Preached on" htmlFor="sermon-date">
              <Input
                id="sermon-date"
                type="date"
                value={form.preachedAt}
                onChange={(e) => set('preachedAt', e.target.value)}
              />
            </Field>
            <Field label="Body format" htmlFor="sermon-format">
              <Select
                id="sermon-format"
                value={form.bodyFormat}
                onChange={(v) => set('bodyFormat', v as BodyFormat)}
              >
                <option value="markdown">Markdown</option>
                <option value="plaintext">Plain text</option>
              </Select>
            </Field>
          </Card>

          <Card className="space-y-5 p-5">
            <Label>Media</Label>
            <Field label="Audio URL" htmlFor="sermon-audio">
              <Input
                id="sermon-audio"
                type="url"
                value={form.audioUrl}
                onChange={(e) => set('audioUrl', e.target.value)}
                placeholder="https://…/sermon.mp3"
              />
            </Field>
            <Field label="Video URL" htmlFor="sermon-video">
              <Input
                id="sermon-video"
                type="url"
                value={form.videoUrl}
                onChange={(e) => set('videoUrl', e.target.value)}
                placeholder="https://…/watch?v=…"
              />
            </Field>
          </Card>

          {showPreview && (
            <Card className="space-y-3 p-5">
              <div className="text-xs font-semibold uppercase tracking-wider text-dim">Preview</div>
              <h2 className="font-display text-xl font-bold">{form.title || 'Untitled'}</h2>
              {(form.series || form.speaker) && (
                <p className="text-xs text-dim">
                  {[form.series, form.speaker].filter(Boolean).join(' · ')}
                </p>
              )}
              {form.summary && <p className="text-sm text-muted">{form.summary}</p>}
              {form.body ? (
                <ContentBody format={form.bodyFormat} body={form.body} />
              ) : (
                <p className="text-sm text-dim">Nothing to preview yet.</p>
              )}
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}
